import { useEffect, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { getVanillaEl } from "../../domBridge";
import {
  subscribeNotifications,
  getCachedNotifications,
  refreshNotifications,
  markNotificationsRead,
} from "../../../notificationsApi";
import { formatRelativeTime } from "../../../utils";
import type { AppNotification } from "../../../types";

function iconFor(type: AppNotification["type"]): string {
  if (type === "like") return "bi-heart-fill";
  if (type === "reply") return "bi-reply-fill";
  if (type === "comment") return "bi-chat-left-text-fill";
  if (type === "mention") return "bi-at";
  if (type === "gift") return "bi-gift-fill";
  if (type === "together_invite") return "bi-people-fill";
  if (type === "party_invite") return "bi-music-note-beamed";
  return "bi-controller"; // game_invite
}

// The comment-family types (reply/like/comment/mention) render as "Name
// <verb> a comment" with the comment text quoted below, using albumId for
// navigation - the original, unchanged shape. gift/together_invite/
// game_invite/party_invite have no comment/album to point at, so they
// render their snippet directly as the headline and navigate via deepLink
// instead.
function isCommentFamily(type: AppNotification["type"]): boolean {
  return type === "like" || type === "reply" || type === "comment" || type === "mention";
}

function verbFor(type: AppNotification["type"]): string {
  if (type === "like") return "liked your comment";
  if (type === "reply") return "replied to your comment";
  if (type === "comment") return "posted a new comment";
  return "mentioned you in a comment";
}

export function NotificationsDialog() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const notifications = useSyncExternalStore(subscribeNotifications, getCachedNotifications);

  useEffect(() => {
    const notificationsBtn = getVanillaEl<HTMLButtonElement>("notificationsBtn");
    if (!notificationsBtn) return;
    const onClick = async () => {
      setOpen(true);
      await refreshNotifications();
      if (getCachedNotifications().some((n) => !n.read)) {
        await markNotificationsRead();
      }
    };
    notificationsBtn.addEventListener("click", onClick);
    return () => notificationsBtn.removeEventListener("click", onClick);
  }, []);

  return (
    <AppDialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
      <AppDialogHeader icon="bi-bell" title="Notifications" onBack={() => setOpen(false)} />
      <DialogContent sx={{ pt: 1 }}>
        <div className="notifications-list">
          {!notifications.length && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
              No notifications yet.
            </Typography>
          )}
          {notifications.map((n) => {
            const commentFamily = isCommentFamily(n.type);
            return (
              <button
                key={n.id}
                type="button"
                className={`notification-item d-flex gap-2 w-100 text-start ${n.read ? "" : "is-unread"}`}
                onClick={() => {
                  setOpen(false);
                  navigate(n.deepLink || `/albums/${n.albumId}`);
                }}
              >
                <span className="notification-icon flex-shrink-0" aria-hidden="true">
                  <i className={`bi ${iconFor(n.type)}`} />
                </span>
                <span className="min-w-0 flex-grow-1">
                  <span className="small">
                    <span className="fw-semibold">{n.actorName}</span> {commentFamily ? verbFor(n.type) : n.snippet}
                  </span>
                  {commentFamily && <span className="d-block small muted text-truncate">"{n.snippet}"</span>}
                  <span className="d-block notification-time muted">
                    {formatRelativeTime(n.createdAt)}
                    {commentFamily && n.albumName ? <> &middot; {n.albumName}</> : null}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </AppDialog>
  );
}
